import type {ReactElement} from 'react';
import {Fragment, useEffect, useRef, useState} from 'react';
import Sortable from 'sortablejs';
import {useEventWizardTicketingConfig} from '@Components/event-wizard/event-wizard.hooks';
import {
  onEventWizardTicketingQuestionAdd,
  onEventWizardTicketingQuestionRemove,
  onEventWizardTicketingQuestionUpdate,
  onEventWizardTicketingQuestionsReorder,
} from '@Components/event-wizard/event-wizard-slice';
import {QuestionInputType} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import type {TicketingQuestion} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {isTicketingQuestionValid, pruneTicketingQuestionOptions, revertSortableDomMove} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {TicketingQuestionCard} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-card/ticketing-question-card';
import {TicketingQuestionEditor} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-editor/ticketing-question-editor';
import {TicketingRequiredPill} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-required-pill/ticketing-required-pill';
import {TicketingPill} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-pill/ticketing-pill';
import {TicketingQuestionBox} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-box/ticketing-question-box';
import {Button, ButtonSize} from '@Components/button';
import {getUpsellOrSecondaryButtonAppearance} from '@Utils/button.util';
import {isModalOpen} from '@Components/modal-container';
import {isUserPremium} from '@Libraries/user.library';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import useOutsideClick from '@Hooks/useOutsideClick';
import {useAppDispatch} from '@/hooks';
import type {TicketingDefaultRow, TicketingQuestionsEditorState} from './ticketing-questions.types';
import {TICKETING_QUESTIONS_DRAG_HANDLE} from './ticketing-questions.types';
import styles from './ticketing-questions.module.scss';

const DEFAULT_ROWS: TicketingDefaultRow[] = [
  {labelKey: 'pmwjs_event_ticketing_q_name', icon: 'icon-user'},
  {labelKey: 'pmwjs_event_ticketing_q_email', icon: 'icon-envelope'},
];

const getNewQuestionDraft = (): TicketingQuestion => {
  return {text: '', type: QuestionInputType.SHORT_ANSWER, required: false, options: []};
};

export function TicketingQuestions(): ReactElement {
  const dispatch = useAppDispatch();
  const config = useEventWizardTicketingConfig();
  const questions = config.questions;

  const savedListRef = useRef<HTMLDivElement | null>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<TicketingQuestionsEditorState | null>(null);
  const [editor, setEditorReactState] = useState<TicketingQuestionsEditorState | null>(null);
  const [isUpsellLoading, setIsUpsellLoading] = useState<boolean>(false);

  // The ONLY safe way to change the editor: keeps editorRef.current in sync with React state so the
  // ref-reading paths (resolveOpenEditor, onCommit, the unmount autosave) never see a stale value.
  // Never call setEditorReactState directly.
  const setEditor = (next: TicketingQuestionsEditorState | null): void => {
    editorRef.current = next;
    setEditorReactState(next);
  };

  const commitEditorState = (state: TicketingQuestionsEditorState): void => {
    const pruned = pruneTicketingQuestionOptions(state.draft);
    if (state.mode === 'add') {
      dispatch(onEventWizardTicketingQuestionAdd(pruned));
    } else {
      dispatch(onEventWizardTicketingQuestionUpdate({index: state.index, question: pruned}));
    }
  };

  /**
   * Single-editor invariant: before any interaction outside the open editor, resolve it — commit
   * if valid (append or in-place update, neither shifts existing indices), else discard — and clear
   * the editor + ref synchronously. Safe to call when no editor is open.
   */
  const resolveOpenEditor = (): void => {
    const open = editorRef.current;
    if (open === null) {
      return;
    }
    if (isTicketingQuestionValid(open.draft)) {
      commitEditorState(open);
    }
    setEditor(null);
  };

  // Clicking outside the open editor dismisses it (B6): commit a valid draft / discard an invalid one
  // and close — same as resolveOpenEditor — so an invalid or empty editor can't get stuck open. Skipped
  // while a modal is up: the question-type dropdown opens as a portaled modal on small screens, and
  // that click lands outside the editor but must not dismiss it.
  const resolveOpenEditorOnOutsideClick = (): void => {
    if (isModalOpen()) {
      return;
    }
    resolveOpenEditor();
  };
  useOutsideClick(editorContainerRef, resolveOpenEditorOnOutsideClick, null, () => editor !== null);

  const onAddQuestion = (): void => {
    resolveOpenEditor();
    setEditor({mode: 'add', index: questions.length, draft: getNewQuestionDraft(), original: null});
  };

  const onEditQuestion = (index: number): void => {
    resolveOpenEditor();
    setEditor({mode: 'edit', index, draft: {...questions[index]}, original: questions[index]});
  };

  const onDeleteQuestion = (index: number): void => {
    resolveOpenEditor();
    dispatch(onEventWizardTicketingQuestionRemove(index));
  };

  const onDraftChange = (draft: TicketingQuestion): void => {
    const open = editorRef.current;
    if (open === null) {
      return;
    }
    setEditor({...open, draft});
    // Live-preview edits by mirroring the draft onto its committed entry — the checkout preview redraws
    // from Redux, so this reflects changes before "Save changes". While the draft is invalid, write back
    // the original so the preview never shows a half-built question (Cancel restores it too). Add mode is
    // not mirrored: a not-yet-saved question would render as a duplicate row.
    if (open.mode === 'edit' && open.original !== null) {
      dispatch(onEventWizardTicketingQuestionUpdate({index: open.index, question: isTicketingQuestionValid(draft) ? pruneTicketingQuestionOptions(draft) : open.original}));
    }
  };

  const onCommit = (): void => {
    const open = editorRef.current;
    if (open === null) {
      return;
    }
    commitEditorState(open);
    setEditor(null);
  };

  const onCancel = (): void => {
    // Undo the live-preview writes: restore the committed question captured at edit open.
    const open = editorRef.current;
    if (open !== null && open.mode === 'edit' && open.original !== null) {
      dispatch(onEventWizardTicketingQuestionUpdate({index: open.index, question: open.original}));
    }
    setEditor(null);
  };

  // Step-navigation autosave: on unmount, commit the still-open draft once iff valid. Reads the ref
  // (latest), not a stale closure; explicit commit/Cancel already nulled the ref, so no double-dispatch.
  useEffect(() => {
    return (): void => {
      const open = editorRef.current;
      if (open !== null && isTicketingQuestionValid(open.draft)) {
        commitEditorState(open);
        editorRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!savedListRef.current || editor !== null) {
      return undefined;
    }

    const sortableInstance = Sortable.create(savedListRef.current, {
      handle: `.${TICKETING_QUESTIONS_DRAG_HANDLE}`,
      // No settle animation: onEnd reverts the DOM move and React re-renders from Redux, so an animation here only leaves a stale transform on the reverted node (the post-drop hitch).
      animation: 0,
      ghostClass: styles.questionGhost,
      onEnd: (event): void => {
        const moved = revertSortableDomMove(event);
        if (!moved || event.oldIndex === undefined || event.newIndex === undefined) {
          return;
        }
        dispatch(onEventWizardTicketingQuestionsReorder({sourceIndex: event.oldIndex, destinationIndex: event.newIndex}));
      },
    });

    return (): void => {
      sortableInstance.destroy();
    };
  }, [editor === null, questions.length]);

  const getDefaultRow = (row: TicketingDefaultRow, displayNumber: number): ReactElement => {
    return (
      <TicketingQuestionBox
        key={row.labelKey}
        displayNumber={displayNumber}
        pills={
          <>
            <TicketingPill icon={row.icon} label={window.i18next.t(row.labelKey)} />
            <TicketingRequiredPill />
          </>
        }
      />
    );
  };

  // Wraps the single open editor with the outside-click ref (B6). Only one editor exists at a time —
  // add or in-place edit — so this ref always lands on exactly the open editor.
  const getEditorForState = (state: TicketingQuestionsEditorState): ReactElement => {
    return (
      <div ref={editorContainerRef} className="_full-width">
        <TicketingQuestionEditor
          draft={state.draft}
          mode={state.mode}
          displayNumber={DEFAULT_ROWS.length + state.index + 1}
          onDraftChange={onDraftChange}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      </div>
    );
  };

  const getQuestionRow = (question: TicketingQuestion, index: number): ReactElement => {
    if (editor !== null && editor.mode === 'edit' && editor.index === index) {
      return <Fragment key={`editor-${index}`}>{getEditorForState(editor)}</Fragment>;
    }

    return (
      <TicketingQuestionCard
        key={`question-${index}`}
        question={question}
        displayNumber={DEFAULT_ROWS.length + index + 1}
        onEdit={(): void => {
          onEditQuestion(index);
        }}
        onDelete={(): void => {
          onDeleteQuestion(index);
        }}
        dragHandleClass={TICKETING_QUESTIONS_DRAG_HANDLE}
      />
    );
  };

  const getAddEditor = (): ReactElement | null => {
    if (editor?.mode !== 'add') {
      return null;
    }
    return getEditorForState(editor);
  };

  const onAddQuestionUpsell = (): void => {
    setIsUpsellLoading(true);
    openEventsUpsellDialog(EventUpsellType.CUSTOM_FORMS, (): void => {
      setIsUpsellLoading(false);
    });
  };

  const getAddQuestionButton = (): ReactElement | null => {
    if (editor !== null && editor.mode === 'add') {
      return null;
    }
    const isPremium = isUserPremium();
    return (
      <div className="bg-neutral-alt-primary-default spacing-p-3 corner-radius-8 _full-width">
        <Button
          customClasses="spacing-p-t-2"
          {...getUpsellOrSecondaryButtonAppearance(!isPremium)}
          size={ButtonSize.SMALL}
          icon={isPremium ? 'icon-plus' : 'icon-crown'}
          isLoading={isPremium ? false : isUpsellLoading}
          text={window.i18next.t('pmwjs_event_ticketing_add_question')}
          onClick={isPremium ? onAddQuestion : onAddQuestionUpsell}
        />
      </div>
    );
  };

  return (
    <div className="gap-3 flex-column-align-start _full-width">
      {DEFAULT_ROWS.map((row, index) => getDefaultRow(row, index + 1))}

      {questions.length > 0 ? (
        <div ref={savedListRef} className="gap-3 flex-column-align-start _full-width">
          {questions.map((question, index) => getQuestionRow(question, index))}
        </div>
      ) : undefined}

      {getAddEditor()}
      {getAddQuestionButton()}
    </div>
  );
}
